import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { get, set } from "lodash";
import {
  TextInput,
  Button,
  P,
  Divider,
  Grid,
  Box,
  H2,
  Spacer,
  Small,
  Flex,
  H3,
  Toggle,
} from "orcs-design-system";
import { useMutation } from "@apollo/client";

import ErrorNotification from "src/components/ErrorNotification";
import FancyCard, {
  FancyCardBody,
  FancyCardFooter,
} from "src/components/FancyCard/FancyCard";
import { updateGlobalConfig } from "src/queries/globalConfig.graphql";
import { useRefetchContext } from "src/contexts/global/GlobalContext";
import { getBrandingValues, BRANDING_KEYS } from "src/hooks/useGetBranding";
import useSaveWorkspaceConfiguration from "src/hooks/useSaveWorkspaceConfiguration";
import { useReloadCurrentWorkspace } from "src/contexts/global/WorkspaceContext";

import ImageDropZone from "./sub-components/ImageDropZone";
import HeaderPreview from "./sub-components/HeaderPreview";
import TeamHeaderEditor from "./sub-components/TeamHeaderEditor";
import ImageLibrary from "./sub-components/ImageLibrary";

const Branding = ({
  globalConfig,
  globalConfigId,
  workspace,
  title = "Here you can set some branding for TeamForm.",
}) => {
  const [inputValues, setInputValues] = useState(() =>
    getBrandingValues(globalConfig, workspace)
  );
  const reloadCurrentWorkspace = useReloadCurrentWorkspace();

  const [key, setKey] = useState(0);
  const [originalValues, setOriginalValues] = useState(inputValues);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const refetchContext = useRefetchContext();

  const isChanged = useMemo(() => {
    return JSON.stringify(inputValues) !== JSON.stringify(originalValues);
  }, [inputValues, originalValues]);

  const [saveAppConfig] = useMutation(updateGlobalConfig);
  const { saveWorkspaceConfiguration } =
    useSaveWorkspaceConfiguration(workspace);

  const handleOnChange = useCallback(
    (name) => (e) => {
      setInputValues((prev) => {
        const value = e?.target?.value;
        const newValues = JSON.parse(JSON.stringify(prev || {}));

        set(newValues, name, value);

        return newValues;
      });
    },
    []
  );

  const handleGradientChange = useCallback((gradient) => {
    setInputValues((prev) => ({
      ...(prev || {}),
      [BRANDING_KEYS.gradients]: gradient,
    }));
  }, []);

  const toggleStockImagesDisabled = useCallback((e) => {
    setInputValues((prev) => ({
      ...(prev || {}),
      stockImagesDisabled: e.target.checked,
    }));
  }, []);

  const handleImageFileUpload = useCallback((base64Str) => {
    setInputValues((prev) => ({
      ...(prev || {}),
      logoImage: {
        ...(prev?.logoImage || {}),
        src: base64Str,
      },
    }));
  }, []);

  const handleOnSave = useCallback(async () => {
    setError(null);
    setIsSaving(true);

    try {
      if (workspace) {
        const tenantBranding = getBrandingValues(globalConfig);
        const isBrandingChanged =
          JSON.stringify(inputValues) !== JSON.stringify(tenantBranding);

        await saveWorkspaceConfiguration({
          branding: isBrandingChanged
            ? inputValues
            : // Set to null to reset to tenant defaults
              { appName: null, gradients: null, logoImage: null },
        });
        await reloadCurrentWorkspace();
      } else {
        const input = {
          id: globalConfigId,
          appName: inputValues.appName,
          gradients: [inputValues.gradients],
          logoImage: inputValues.logoImage,
          stockImagesDisabled: inputValues.stockImagesDisabled,
        };

        await saveAppConfig({
          variables: {
            input,
          },
        });
      }

      await refetchContext();
      setOriginalValues(inputValues);
    } catch (e) {
      setError(e);
    } finally {
      setIsSaving(false);
    }
  }, [
    inputValues,
    globalConfigId,
    refetchContext,
    saveAppConfig,
    saveWorkspaceConfiguration,
    workspace,
    reloadCurrentWorkspace,
    globalConfig,
  ]);

  const resetToDefault = useCallback(() => {
    setInputValues(getBrandingValues(globalConfig));

    setKey((prev) => prev + 1);
  }, [globalConfig]);

  const revertChanges = useCallback(() => {
    setInputValues(originalValues);
    setKey((prev) => prev + 1);
  }, [originalValues]);

  const canResetToDefault = useMemo(() => {
    return (
      JSON.stringify(inputValues) !==
      JSON.stringify(getBrandingValues(globalConfig))
    );
  }, [inputValues, globalConfig]);

  return (
    <FancyCard height="100%">
      <FancyCardBody>
        <Box p="xs">
          <Box mb="r">
            <H2 mb="xs" fontSize="2rem">
              {title}
            </H2>
          </Box>
          <Divider />
          <Grid gridTemplateColumns="1fr 1fr" gridGap="l">
            <Box pt="r">
              <Spacer mb="r">
                {!workspace && (
                  <TextInput
                    type="text"
                    id="appName"
                    label="App name"
                    placeholder="TeamForm"
                    value={get(inputValues, BRANDING_KEYS.appName, "")}
                    onChange={handleOnChange(BRANDING_KEYS.appName)}
                    disabled={isSaving}
                  />
                )}
                <Box>
                  <P mb="s">Upload your company logo</P>
                  <Small display="block" mb="s">
                    Supports uploading a .png or .svg file. SVG is preferable
                    for clarity on high resolution screens.
                  </Small>
                  <Small display="block" mb="s">
                    Ideally the logo should be either an equal height/width
                    logomark, or a full horizontal logo.
                  </Small>
                  <Small display="block">
                    If using PNG ideal height is 100px, if using SVG ideal
                    height is 50px.
                  </Small>
                </Box>
                <ImageDropZone
                  onFileImported={handleImageFileUpload}
                  btnLabel="Upload"
                  disabled={isSaving}
                />
                <TextInput
                  name="appLogoAlt"
                  type="text"
                  id="logo-image-alt"
                  label="App logo image alt"
                  placeholder="Company name"
                  value={get(inputValues, BRANDING_KEYS.logoImage_alt, "")}
                  onChange={handleOnChange(BRANDING_KEYS.logoImage_alt)}
                  disabled={isSaving}
                />
                <TextInput
                  name="appLogoStyles"
                  type="text"
                  id="logo-image-styles"
                  label="App logo image styles"
                  placeholder="CSS"
                  fullWidth
                  value={get(inputValues, BRANDING_KEYS.logoImage_styles, "")}
                  onChange={handleOnChange(BRANDING_KEYS.logoImage_styles)}
                  disabled={isSaving}
                />
              </Spacer>
            </Box>
            <Box pt="r">
              <P mb="s">
                <strong>App header preview:</strong>
              </P>
              <HeaderPreview config={inputValues} />
            </Box>
          </Grid>

          <Divider />

          <TeamHeaderEditor
            key={key}
            value={get(inputValues, BRANDING_KEYS.gradients, "")}
            onChange={handleGradientChange}
            disabled={isSaving}
          />
        </Box>
        {!workspace && (
          <Box py="xl" height="800px">
            <Divider mb="xl" />
            <Flex>
              <Box mb="xl">
                <H3>Upload a selection of brand approved imagery</H3>
                <P>
                  These will be available for users to select as images for
                  teams, people or tags.
                </P>
              </Box>
              <Box ml="auto">
                <Toggle
                  id="stock-images-disabled"
                  label="Stock images disabled"
                  checked={get(
                    inputValues,
                    BRANDING_KEYS.stockImagesDisabled,
                    false
                  )}
                  onChange={toggleStockImagesDisabled}
                />
              </Box>
            </Flex>
            <ImageLibrary />
          </Box>
        )}
      </FancyCardBody>
      <FancyCardFooter>
        <Flex gap="s">
          <Button
            disabled={!isChanged}
            onClick={handleOnSave}
            isLoading={isSaving}
            variant={isSaving ? "disabled" : "success"}
          >
            Save changes
          </Button>
          <Button
            disabled={!isChanged}
            onClick={revertChanges}
            isLoading={isSaving}
            variant="ghost"
          >
            Revert changes
          </Button>

          <Button
            disabled={!canResetToDefault}
            onClick={resetToDefault}
            isLoading={isSaving}
            variant="ghost"
          >
            Reset to tenant defaults
          </Button>
          {error && <ErrorNotification error={error} />}
        </Flex>
      </FancyCardFooter>
    </FancyCard>
  );
};

Branding.propTypes = {
  globalConfig: PropTypes.object,
  globalConfigId: PropTypes.string,
  workspace: PropTypes.object,
  title: PropTypes.string,
};

export default Branding;
